/**
 * IMPORTANT: Partially duplicated in eataliano-frontend-ordering-site: menu entities -> utils
 */
import {AvailableCategory, AvailableMenu, Category, DayAvailability, Menu, Product, ProductStock} from "@/data/entities/menu/index";
import {FulfillmentType} from "../restaurant";

type Day =
    "monday" |
    "tuesday" |
    "wednesday" |
    "thursday" |
    "friday" |
    "saturday" |
    "sunday";

const days: Day[] = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

/**
 * Removes categories based on their availability, and sorts products on the
 * availability/in-stock state.
 */
export const filterAndSortOnAvailability = (
    menu: Menu,
    fulfillmentType: FulfillmentType,
    restaurantId: number,
): AvailableMenu => {
    // Filter, sort & fill categories:
    const today: Day = getToday();
    const filteredAndSortedCategories: AvailableCategory[] = [];
    for (let i = 0; i < menu.categories.length; i++) {
        const category = menu.categories[i];

        // Skip category that shouldn't be available now
        if (!isCategoryAvailableNow(category, today)) {
            continue;
        }

        // Category with products
        const availableCategory: AvailableCategory = {
            name: category.name,
            subtitle: category.subtitle,
            availability: category.availability,
            productIds: category.productIds,
            sortedProducts: sortProductsOnAvailability(category, menu, fulfillmentType),
        };
        filteredAndSortedCategories.push(availableCategory);
    }

    return {
        restaurantId,
        fulfillmentType,
        featuredProducts: menu.featuredProducts,
        loyaltyRewards: menu.loyaltyRewards,
        products: menu.products,
        optionGroups: menu.optionGroups,
        options: menu.options,
        categories: filteredAndSortedCategories,
    };
}

const getToday = (): Day => {
    const today = new Date();
    return days[today.getDay()];
};

const parseTime = (inputTime: string | null): Date | null => {
    if (inputTime === null) {
        return null;
    }

    const hours = parseInt(inputTime.slice(0, 2), 10);
    const minutes = parseInt(inputTime.slice(2), 10);
    const d = new Date();
    d.setHours(hours, minutes, 0, 0);

    return d;
};

/**
 * Returns whether the given category is available at this moment. Based on the
 * current local datetime and DayAvailabilities of the category.
 */
const isCategoryAvailableNow = (category: Category, today: Day): boolean => {
    // Get availabilities based on current day
    const availabilitiesToday = category
        .availability
        .filter((avail) => avail.day === today);

    // For each availability for today, check is the category is available
    // at this moment in time:
    for (let i = 0; i < availabilitiesToday.length; i++) {
        const {disabled, timeFrom, timeUntil}: DayAvailability = availabilitiesToday[i];

        // "Category disabled on this weekday. Note disabled is mutually
        // exclusive with until." So, if `disabled` it `true`, then this
        // category is not available today.
        if (disabled) {
            return false;
        }

        const parsedTimeFrom: Date | null = parseTime(timeFrom);
        const parsedTimeUntil: Date | null = parseTime(timeUntil);
        const now = new Date();

        // Cat. available in a timeslot today
        if (parsedTimeFrom !== null
            && parsedTimeUntil !== null
            && (now < parsedTimeFrom || now > parsedTimeUntil)) {
            return false;
        }

        // Cat. available until some time today
        if (parsedTimeFrom === null
            && parsedTimeUntil !== null
            && now > parsedTimeUntil) {
            return false;
        }

        // Cat. available from a starting time today
        if (parsedTimeFrom !== null
            && parsedTimeUntil === null
            && now < parsedTimeFrom) {
            return false;
        }
    }

    return true;
};

export const filterOnSearch = (menu: AvailableMenu, searchQuery: string): AvailableMenu => {
    const normalizedSearchQuery = searchQuery.toLowerCase().trim();
    if (normalizedSearchQuery.length === 0) {
        return menu;
    }

    return {
        ...menu,
        categories: menu.categories.map(c => ({
            ...c,
            sortedProducts: c.sortedProducts.filter(({name, description}) =>
                name.toLowerCase().includes(normalizedSearchQuery)
                || description.toLowerCase().includes(normalizedSearchQuery)),
        })),
    };
};


export const findProductById = (productId: number, list: Product[]): Product => {
    const result = list.find((value: Product) => value.id === productId);

    if (!result)
        throw new Error(`Product ${productId} not found!`)

    return result;
}

/**
 * Sort and maps the product IDs to Products. Sorting is based on fulfillment-
 * type and sold out state. Products not available for the current fulfillment-
 * type or that are sold out, are placed at the end of the list.
 */
export const sortProductsOnAvailability = (
    category: Category,
    menu: Menu,
    fulfillmentType: FulfillmentType,
): Product[] => {
    const availableProducts: Product[] = [];
    const differentFulfillmentTypeProducts: Product[] = [];
    const soldOutProducts: Product[] = [];

    for (let j = 0; j < category.productIds.length; j++) {
        const product: Product = findProductById(category.productIds[j], menu.products);
        // In the future: Do we want to filter this server-side?
        if (product.availability.length === 0 /* TODO - Is this possible? */
            || product.availability.includes("unavailable")) {
            continue;
        }

        if (product.soldout) {
            soldOutProducts.push(product);
            continue;
        }

        if (!product.availability.includes(fulfillmentType.toLowerCase())) {
            differentFulfillmentTypeProducts.push(product);
            continue;
        }

        availableProducts.push(product);
    }

    return availableProducts.concat(differentFulfillmentTypeProducts, soldOutProducts)
}

/**
 * Copy of the menu in which the product is sold out or in stock, in the
 * product list as well as in the categories.
 */
export const withProductStock = (menu: AvailableMenu, stock: ProductStock): AvailableMenu => {
    const update = (product: Product): Product => product.id === stock.id
        ? {...product, soldout: stock.soldout}
        : product;

    return {
        ...menu,
        products: menu.products.map(update),
        categories: menu.categories.map(category => ({
            ...category,
            sortedProducts: category.sortedProducts.map(update),
        })),
    };
};
